import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/modules/auth/authApi";
import { setAuth } from "@/shared/hooks/useAuth";
import { extractApiError } from "@/shared/api/apiClient";
import { useActionNotice } from "@/shared/components/ActionNotice";
import { clearTahtaDrawings } from "@/modules/tahta/tahtaDrawingStore";

/**
 * Oturum-içi okul değiştirme akışı. Sıra kritik: önce yeni token+user yazılır (sonraki istekler yeni
 * okulla gider), sonra uçuştaki eski-token istekleri iptal edilip TÜM react-query cache'i boşaltılır
 * (query anahtarlarında schoolId yok — eski okulun verisi bir an bile görünmemeli; invalidate değil
 * clear), en son ana sayfaya dönülür. Hata olursa mevcut oturum/okul olduğu gibi kalır.
 */
export const useSchoolSwitch = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notice = useActionNotice();
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const switchTo = async (recordId: string, schoolName?: string) => {
    if (switchingId) return;
    setSwitchingId(recordId);
    try {
      const res = await authApi.switchSchool(recordId);
      if (!res.accessToken || !res.user) {
        // extractApiError düz Error.message'ı aynen gösterir — kullanıcıya dönük Türkçe metin.
        throw new Error("Okul değişimi başarısız oldu, lütfen tekrar deneyin");
      }
      setAuth(res.accessToken, res.user);
      clearTahtaDrawings(); // okul-yerel durum — çıkıştaki temizlikle aynı
      await queryClient.cancelQueries();
      queryClient.clear();
      navigate("/", { replace: true });
      notice.show({ phase: "done", label: `${schoolName ?? "Okul"} paneline geçildi` });
    } catch (err) {
      notice.show({ phase: "error", label: extractApiError(err) });
    } finally {
      setSwitchingId(null);
    }
  };

  return { switchTo, switchingId };
};
